import DateFieldUtils from "@/utils/common/DateFieldUtils.ts";
import Translator from "@/utils/common/Translator.ts";
import dayjs from "@/utils/dayjs.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import ChatMessageType from "@/utils/constants/ChatMessageType.ts";
import router from "@/router";
import TopPanelSearchUtils, {type SearchItem} from "@/utils/common/TopPanelSearchUtils.ts";
import IdsAndConstants from "@/utils/UIConstants/IdsAndConstants.ts";
import ChatParticipantRole from "@/utils/constants/ChatParticipantRole.ts";
import DTOChatMessage = namaTypes.DTOChatMessage;
import DTOChatConversation = namaTypes.DTOChatConversation;

export default class ChatUtils {

  static readonly IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp', 'ico']);
  static readonly VIDEO_EXTENSIONS = new Set(['mp4', 'avi', 'mov', 'mkv', 'webm', 'ogg', 'ogv', 'm4v']);
  static readonly AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'ogg', 'aac', 'flac', 'wma', 'm4a', 'opus']);

  private static FILE_ICON_MAP: Record<string, string> = {
    pdf: 'picture_as_pdf', doc: 'description', docx: 'description',
    xls: 'table_chart', xlsx: 'table_chart', csv: 'table_chart',
    ppt: 'slideshow', pptx: 'slideshow', zip: 'folder_zip',
    rar: 'folder_zip', '7z': 'folder_zip', png: 'image',
    jpg: 'image', jpeg: 'image', gif: 'image',
    svg: 'image', webp: 'image', mp4: 'movie',
    avi: 'movie', mov: 'movie', mkv: 'movie',
    mp3: 'audio_file', wav: 'audio_file', ogg: 'audio_file',
    txt: 'text_snippet', json: 'data_object', xml: 'code',
  };

  static readonly MENTION_REGEX = /\[@([^\]]+)]\(([^)]+)\)/g;

  static readonly MENTION_ENTITY_STYLES = [
    {bg: '#374151', text: '#f3f4f6', badge: '#4b5563'},
    {bg: '#1e40af', text: '#dbeafe', badge: '#2563eb'},
    {bg: '#065f46', text: '#d1fae5', badge: '#059669'},
    {bg: '#7c2d12', text: '#fed7aa', badge: '#c2410c'},
    {bg: '#581c87', text: '#f3e8ff', badge: '#7c3aed'},
    {bg: '#9f1239', text: '#ffe4e6', badge: '#e11d48'},
  ];

  static EMOJI_CATEGORIES: Record<string, { icon: string; emojis: string[] }> = {
    smileys: {
      icon: '😀',
      emojis: [
        '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '😊',
        '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙', '🥲', '😋',
        '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🫢', '🤫', '🤔',
        '🫡', '🤐', '🤨', '😐', '😑', '😶', '🫥', '😏', '😒', '🙄',
        '😬', '🤥', '😌', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕',
        '🤢', '🤮', '🥵', '🥶', '🥴', '😵', '🤯', '🤠', '🥳', '🥸',
        '😎', '🤓', '🧐', '😕', '🫤', '😟', '🙁', '😮', '😯', '😲',
        '😳', '🥺', '🥹', '😦', '😧', '😨', '😰', '😥', '😢', '😭',
        '😱', '😖', '😣', '😞', '😓', '😩', '😫', '🥱', '😤', '😡',
        '😠', '🤬', '😈', '👿', '💀', '☠️', '💩', '🤡', '👹', '👺',
      ]
    },
    gestures: {
      icon: '👋',
      emojis: [
        '👋', '🤚', '🖐️', '✋', '🖖', '🫱', '🫲', '🫳', '🫴', '👌',
        '🤌', '🤏', '✌️', '🤞', '🫰', '🤟', '🤘', '🤙', '👈', '👉',
        '👆', '🖕', '👇', '☝️', '🫵', '👍', '👎', '✊', '👊', '🤛',
        '🤜', '👏', '🙌', '🫶', '👐', '🤲', '🤝', '🙏', '💪', '🦾',
        '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔',
        '❤️‍🔥', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟',
      ]
    },
    animals: {
      icon: '🐶',
      emojis: [
        '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐻‍❄️', '🐨',
        '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🙈', '🙉', '🙊', '🐒',
        '🐔', '🐧', '🐦', '🐤', '🦆', '🦅', '🦉', '🦇', '🐺', '🐗',
        '🐴', '🦄', '🐝', '🪱', '🐛', '🦋', '🐌', '🐞', '🐜', '🪲',
        '🌸', '🌺', '🌻', '🌹', '🌷', '🌼', '💐', '🌿', '☘️', '🍀',
      ]
    },
    food: {
      icon: '🍕',
      emojis: [
        '🍎', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐', '🍈',
        '🍒', '🍑', '🥭', '🍍', '🥥', '🥝', '🍅', '🥑', '🍆', '🌶️',
        '🫑', '🥒', '🥬', '🥦', '🧄', '🧅', '🍄', '🥜', '🫘', '🌰',
        '🍕', '🍔', '🍟', '🌭', '🍿', '🧂', '🥚', '🍳', '🧇', '🥞',
        '☕', '🍵', '🧃', '🥤', '🍶', '🍺', '🍻', '🥂', '🍷', '🍹',
      ]
    },
    objects: {
      icon: '⚽',
      emojis: [
        '⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🥏', '🎱',
        '🏓', '🏸', '🏒', '🥅', '⛳', '🏹', '🎣', '🤿', '🥊', '🥋',
        '🎯', '🪀', '🪁', '🔮', '🧿', '🎮', '🕹️', '🎰', '🎲', '🧩',
        '🎭', '🎨', '🎬', '🎤', '🎧', '🎼', '🎹', '🥁', '🎷', '🎺',
        '🎸', '🪕', '🎻', '🎪', '🎗️', '🏅', '🥇', '🥈', '🥉', '🏆',
      ]
    },
  };


  static filterUsers(users: any[], search: string): any[] {
    const q = search?.toLowerCase()?.trim();
    if (!q) return users;
    return users.filter(user => {
      const fields = [user.id, user.name1, user.name2, user.code, user.actualCode];
      return fields.some(f => f?.toString().toLowerCase().includes(q));
    });
  }

  static getLastMessageDateLabel(value: Date | string | undefined): string {
    let dateLabel = ChatUtils.getChatDateLabel(value);
    if (!dateLabel || dateLabel === Translator.DATE_LABELS.today)
      return dayjs(value).format('HH:mm');
    return dateLabel;
  }

  static getChatDateLabel(value: Date | string | undefined): string {
    const date: Date = DateFieldUtils.toDate(value);
    if (!date || !date.getTime())
      return '';
    const now = new Date();
    const today = new Date(now.setHours(0, 0, 0, 0));
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const last7Days = new Date(today);
    last7Days.setDate(today.getDate() - 6);
    if (date >= today) return Translator.DATE_LABELS.today;
    if (date >= yesterday) return Translator.DATE_LABELS.yesterday;
    if (date >= last7Days) {
      return date.toLocaleDateString(Translator.isArabic() ? 'ar-EG' : 'en-GB', {weekday: 'long'});
    }
    return date.toLocaleDateString(Translator.isArabic() ? 'ar-EG' : 'en-GB');
  }

  static async getUnreadCount(userId: string | undefined): Promise<number> {
    if (!userId)
      return 0;
    const response = await NamaCommon.namaFetch(NamaURLS.UNREAD_COUNT + `?userId=${userId}&userType=user`, {
      method: "GET",
      doNotUseMainReqForHttp: true,
      noLoadingIndicator: true
    })
    if (ObjectChecker.isNotNullOrUndefined(response)) {
      return response;
    }
    return 0;
  }

  // TODO :: remove it if useless
  static async getUnreadCountsByConversation(userId: string, userType: string): Promise<Record<string, number>> {
    const response = await NamaCommon.namaFetch(NamaURLS.UNREAD_BY_CONVERSATION + `?userId=${userId}&userType=${userType}`, {
      method: "GET",
      doNotUseMainReqForHttp: true,
      noLoadingIndicator: true
    });
    console.log(response);
    if (NamaCommon.isSuccess(response) && response.length > 0) {
      return response;
    }
    return {};
  }

  static shouldShowDateLabel(chatMessages: DTOChatMessage, previousChatMessages: DTOChatMessage | undefined): boolean {
    if (!previousChatMessages)
      return true;
    const currentDate = DateFieldUtils.toDate(chatMessages.sentAt);
    const previousDate = DateFieldUtils.toDate(previousChatMessages.sentAt);
    return !ChatUtils.isSameDay(currentDate, previousDate);
  }

  static isSameDay(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  static getFileIcon(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
    return ChatUtils.FILE_ICON_MAP[ext] || 'insert_drive_file';
  }

  static formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  static getFileExtension(fileName: string | undefined): string {
    if (!fileName) return '';
    return fileName.split('.').pop()?.toLowerCase() ?? '';
  }

  static getMessageTypeForFile(fileName: string | undefined): string {
    const ext = ChatUtils.getFileExtension(fileName);
    if (!ext) return 'File';
    if (ChatUtils.IMAGE_EXTENSIONS.has(ext)) return 'Image';
    if (ChatUtils.VIDEO_EXTENSIONS.has(ext)) return 'Video';
    if (ChatUtils.AUDIO_EXTENSIONS.has(ext)) return 'Audio';
    return 'File';
  }

  static resolveEffectiveMessageType(msg: DTOChatMessage): ChatMessageType {
    const declared = msg.messageType as ChatMessageType;
    if (declared && declared !== 'File') return declared;
    if (declared === 'File' || msg.attachment?.attachmentInfo?.fileName) {
      return ChatUtils.getMessageTypeForFile(msg.attachment?.attachmentInfo?.fileName);
    }
    return declared || 'Text';
  }

  static isImageMessage(msg: DTOChatMessage): boolean {
    return ChatUtils.resolveEffectiveMessageType(msg) === 'Image';
  }

  static isVideoMessage(msg: DTOChatMessage): boolean {
    return ChatUtils.resolveEffectiveMessageType(msg) === 'Video';
  }

  static isAudioMessage(msg: DTOChatMessage): boolean {
    return ChatUtils.resolveEffectiveMessageType(msg) === 'Audio';
  }

  static isMediaMessage(msg: DTOChatMessage): boolean {
    const type = ChatUtils.resolveEffectiveMessageType(msg);
    return type === 'Image' || type === 'Video' || type === 'Audio';
  }

  static isGenericFileMessage(msg: DTOChatMessage): boolean {
    return ChatUtils.resolveEffectiveMessageType(msg) === 'File' && !!msg.attachment?.attachmentInfo;
  }

  static getFileDownloadUrl(fileId: string | undefined): string {
    if (!fileId) return '';
    return `file.download?fileID=${fileId}&entitytype=ChatMessage&ischanged=true`;
  }

  static getThumbnailUrl(fileId: string | undefined): string {
    if (!fileId) return '';
    return `file.download?fileID=${fileId}&entitytype=ChatMessage&ischanged=true&purpose=THUMBNAIL`;
  }

  static getMessagePreviewIcon(messageType?: string): string | undefined {
    switch (messageType) {
      case 'Image': return 'image';
      case 'Video': return 'videocam';
      case 'Audio': return 'mic';
      case 'File': return 'attachment';
      default: return undefined;
    }
  }

  static getMessagePreviewText(msg: DTOChatMessage): string {
    if (msg.isSystemMessage && msg.messageContent) {
      return ChatUtils.getSystemMessagePreview(msg.messageContent);
    }
    let messageContent = msg.messageContent;
    if (messageContent?.trim()) {
      return this.prepareMessageContent(messageContent);
    }
    const type = ChatUtils.resolveEffectiveMessageType(msg);
    switch (type) {
      case ChatMessageType.Image:
        return Translator.translateId('photo');
      case ChatMessageType.Video:
        return Translator.translateId('video');
      case ChatMessageType.Audio:
        return Translator.translateId('audio');
      case ChatMessageType.File:
        return (msg.attachment?.attachmentInfo?.fileName ?? Translator.translateId('file'));
      default:
        return '';
    }
  }

  static getSystemMessagePreview(content: string, currentUserId?: string): string {
    const parts = content.split(':');
    const action = parts[0];
    const targetUserId = parts[1];
    const targetName = parts.slice(2).join(':');
    const isTargetSelf = currentUserId != null && targetUserId === currentUserId;
    if (action === IdsAndConstants.MEMBER_ADDED) {
      if (isTargetSelf)
        return Translator.translateId('chatYouWereAdded');
      return (targetName || '') + ' ' + Translator.translateId('chatAddedToGroup');
    }
    if (action === IdsAndConstants.MEMBER_REMOVED) {
      if (isTargetSelf)
        return Translator.translateId('chatYouWereRemoved');
      return (targetName || '') + ' ' + Translator.translateId('chatRemovedFromGroup');
    }
    return content;
  }

  private static prepareMessageContent(messageContent: string) {
    messageContent = messageContent.replace(/\[([^\]]+)\]\([^)]*\)?/g, '$1')
    if (messageContent?.length > 50)
      messageContent = messageContent?.slice(0, 50);
    return messageContent;
  }

  static getLastMessagePreview(chat: DTOChatConversation, isCurrentUserBlocked: string | undefined, currentUserId: string | undefined): string {
    if (isCurrentUserBlocked === ChatParticipantRole.Blocked)
      return Translator.translateId('chatYouWereRemoved');
    if (chat.lastMessageType === ChatMessageType.System && chat.lastMessagePreview)
      return ChatUtils.getSystemMessagePreview(chat.lastMessagePreview, currentUserId);
    if (chat.lastMessagePreview)
      return this.prepareMessageContent(chat.lastMessagePreview ?? '');
    if (chat.lastMessageType) {
      switch (chat.lastMessageType) {
        case ChatMessageType.Image:
          return Translator.translateId('photo');
        case ChatMessageType.Video:
          return Translator.translateId('video');
        case ChatMessageType.Audio:
          return Translator.translateId('voiceMessage');
        case ChatMessageType.File:
          return Translator.translateId('file');
      }
    }
    return '';
  }

  static buildMentionHref(item: SearchItem): string {
    const route = TopPanelSearchUtils.resolveSearchItemRoute(item);
    if (!route) return '';
    return router.resolve(route).href;
  }

  private static hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash);
  }

  static getEntityStyle(item: SearchItem) {
    const entityType = item.record?.entityType ?? '';
    return ChatUtils.MENTION_ENTITY_STYLES[ChatUtils.hashString(entityType) % ChatUtils.MENTION_ENTITY_STYLES.length];
  }

  static getRecordName(item: SearchItem): string {
    if (!item.record)
      return item.text;
    return Translator.translateRef(item.record) || item.text;
  }

  static getInitials(item: SearchItem): string {
    const name = ChatUtils.getRecordName(item);
    if (!name) return '?';
    const words = name.trim().split(/\s+/).filter(w => w.length > 0);
    if (words.length >= 2)
      return (Array.from(words[0])[0] + Array.from(words[1])[0]).toUpperCase();
    const chars = Array.from(name.trim());
    return (chars.length >= 2 ? chars[0] + chars[1] : chars[0] || '?').toUpperCase();
  }

  static getBadgeStyle(item: SearchItem) {
    const s = ChatUtils.getEntityStyle(item);
    return {
      backgroundColor: s.badge,
      color: 'white',
      padding: '2px 10px',
      borderRadius: '999px',
      fontSize: '11px',
      fontWeight: '500',
      whiteSpace: 'nowrap',
      flexShrink: '0',
    };
  }

  static getSecondaryInfo(item: SearchItem): string {
    if (!item.record) return '';
    const parts: string[] = [];
    const code = item.record.code || item.record.altCode;
    if (code) parts.push(code);
    const desc = Translator.isArabic() ? item.record.descriptor1 : item.record.descriptor2;
    if (desc) parts.push(desc);
    return parts.join(' \u00B7 ');
  }

  static getEntityLabel(item: SearchItem): string {
    if (!item.record?.entityType) return '';
    return Translator.translateId(item.record.entityType) ?? item.record.entityType;
  }

  static mentionItemKey(item: SearchItem, idx: number): string {
    return item.record?.id ?? item.id ?? String(idx);
  }

  static getMentionDropdownTitle(phase: 'member' | 'entity' | 'record'): string {
    if (phase === 'member')
      return Translator.translateId('mentionMentionMember') ?? 'Mention member';
    return phase === 'entity'
      ? (Translator.translateId('mentionSelectType') ?? 'Select type')
      : (Translator.translateId('mentionSearchRecords') ?? 'Search records');
  }

  static renderMentions(text: string, isSent: boolean): string {
    const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const linkClass = isSent ? 'chat-mention-link chat-mention-link-send' : 'chat-mention-link';
    return escaped.replace(/\[@([^\]]+)]\(([^)]+)\)/g, (_, name, link) => {
      const safeName = name.replace(/"/g, '&quot;');
      const safeLink = link.replace(/"/g, '&quot;');
      return `<a href="${safeLink}" class="${linkClass}">@${safeName}</a>`;
    });
  }

  static getDisplayTextLength(content: string | undefined): number {
    if (!content) return 0;
    return content.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').length;
  }

  static extractNodeContent(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    const el = node as HTMLElement;
    if (el.classList.contains('mention-chip')) {
      const name = (el.textContent ?? '').substring(1);
      return `[@${name}](${el.dataset.link ?? ''})`;
    }
    if (el.tagName === 'BR') return '\n';
    let result = '';
    for (const child of Array.from(el.childNodes)) {
      result += ChatUtils.extractNodeContent(child);
    }
    return result;
  }

  static extractMessageContent(el: HTMLElement): string {
    let result = '';
    for (const node of Array.from(el.childNodes)) {
      result += ChatUtils.extractNodeContent(node);
    }
    return result.trim();
  }

  static getPlainText(el: HTMLElement): string {
    let result = '';
    for (const node of Array.from(el.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE) {
        result += node.textContent ?? '';
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        if (element.tagName === 'BR') {
          result += '\n';
        } else {
          result += element.textContent ?? '';
        }
      }
    }
    return result;
  }
}
